import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { clearStoredPrincipal } from '../context/principalStorage';

const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:7001/api';

interface User {
  id: string;
  email: string;
  displayName: string;
  tenantId: string;
  role: string;
  isTenantAdmin: boolean;
  /**
   * Whether the user has MFA on. `undefined` = not yet known: the login and
   * refresh responses don't carry it, so the bootstrap `/auth/me` fills it in
   * after every sign-in. Never assume `false` (it used to be hard-coded, which
   * suggested setting up MFA to users who had it).
   */
  mfaEnabled?: boolean;
  /**
   * When guided first-run was completed (or skipped). `undefined` = not yet
   * fetched (the login response doesn't carry it — a bootstrap `/auth/me`
   * fills it in); `null` = explicitly not onboarded (route to setup); a
   * date string = done. The gate only redirects on `null`.
   */
  onboardingCompletedAt?: string | null;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: User | null;
  isAuthenticated: boolean;
  requiresMfa: boolean;
  mfaToken: string | null;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<LoginResult>;
  verifyMfa: (code: string) => Promise<void>;
  logout: () => void;
  ensureValidToken: () => Promise<string>;
  /** Mark the current user as onboarded locally (after complete/skip) so the
   *  gate releases without a reload — the server is already updated. */
  markOnboardingComplete: () => void;
  /** Record that the user just turned MFA on, so MFA prompts go away without
   *  a reload. */
  markMfaEnabled: () => void;
  /**
   * Apply a token response from a non-password auth flow (passkey, magic link).
   * Updates the in-memory auth state synchronously so the SPA re-renders into
   * the authenticated tree without needing a full page reload — important on
   * the OAuth authorize path where the URL query string must survive.
   */
  applyTokens: (data: any) => void;
}

/// Result of a login attempt — callers need to know whether the
/// backend asked for MFA so the page can show the MFA challenge form
/// instead of starting the leave-veil exit transition.
export interface LoginResult {
  requiresMfa: boolean;
  /// Set when the user logged in successfully but cannot continue
  /// until they click the reset/confirm link in their email (new
  /// tenant signups have `EmailConfirmed=false + MustResetPassword=true`,
  /// for which the backend deliberately doesn't return a reset token —
  /// the token lives in the welcome email instead). LoginPage shows
  /// this copy inline rather than redirecting to a broken
  /// /reset-password page with an empty token.
  checkEmailMessage?: string;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}

function parseJwt(token: string): Record<string, any> | null {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

// Bumped when the meaning of a persisted field changes. v2: `mfaEnabled` comes
// from /auth/me — v1 sessions stored a hard-coded false, so a restored v1
// session re-learns it once.
const AUTH_STORAGE_VERSION = 2;

function isTokenExpired(token: string): boolean {
  const payload = parseJwt(token);
  if (!payload?.exp) return true;
  return Date.now() >= payload.exp * 1000 - 60000; // 1 min buffer
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    const stored = sessionStorage.getItem('auth');
    if (stored) {
      try {
        const { v, ...parsed } = JSON.parse(stored);
        if (parsed.accessToken && !isTokenExpired(parsed.accessToken)) {
          const user = parsed.user && v !== AUTH_STORAGE_VERSION
            ? { ...parsed.user, mfaEnabled: undefined }
            : parsed.user;
          return { ...parsed, user, requiresMfa: false, mfaToken: null };
        }
      } catch { /* ignore */ }
    }
    return {
      accessToken: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      requiresMfa: false,
      mfaToken: null,
    };
  });

  useEffect(() => {
    if (state.isAuthenticated) {
      sessionStorage.setItem('auth', JSON.stringify({
        v: AUTH_STORAGE_VERSION,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: true,
      }));
    } else {
      sessionStorage.removeItem('auth');
    }
  }, [state.isAuthenticated, state.accessToken, state.refreshToken, state.user]);

  // The user id the /auth/me bootstrap last ran for (see the effect below).
  const userFactsFetchedForRef = useRef<string | null>(null);

  const markOnboardingComplete = useCallback(() => {
    setState(prev => prev.user
      ? { ...prev, user: { ...prev.user, onboardingCompletedAt: new Date().toISOString() } }
      : prev);
  }, []);

  const markMfaEnabled = useCallback(() => {
    sessionStorage.removeItem('mfaSuggested');
    sessionStorage.removeItem('mfaRequired');
    setState(prev => prev.user ? { ...prev, user: { ...prev.user, mfaEnabled: true } } : prev);
  }, []);

  const applyTokenResponse = useCallback((data: any) => {
    setState(prev => {
      const wasAuthenticated = prev.isAuthenticated;
      // Fresh sign-in (any path — password, passkey, OAuth callback,
      // magic-link callback, signup auto-login) clears the per-day
      // "brief dismissed" flag so the Executive lands on /brief next.
      // Token refresh keeps the flag intact — we only want to bounce
      // the user to the Brief on an actual session transition.
      if (!wasAuthenticated) {
        try { localStorage.removeItem('cadence_brief_dismissed_date'); } catch { /* private browsing */ }
      }
      // A token refresh is the same account: keep what /auth/me told us. A
      // fresh sign-in (or a different account) starts unknown, so the
      // bootstrap below fetches it.
      const sameUser = !!prev.user && !!data.userId && prev.user.id === data.userId;
      return {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        user: {
          id: data.userId || '',
          email: '',
          displayName: data.displayName || '',
          tenantId: data.tenantId || '',
          role: data.role || '',
          isTenantAdmin: data.isTenantAdmin || false,
          mfaEnabled: sameUser ? prev.user?.mfaEnabled : undefined,
          // Login/refresh responses don't carry this — keep any value we
          // already learned (token refresh) so we don't re-trigger the
          // bootstrap fetch; a fresh login leaves it undefined → fetched.
          onboardingCompletedAt: data.onboardingCompletedAt ?? prev.user?.onboardingCompletedAt,
        },
        isAuthenticated: true,
        requiresMfa: false,
        mfaToken: null,
      };
    });
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    const { data } = await axios.post(`${API_URL}/auth/login`, { email, password });

    if (data.requiresMfa) {
      setState(prev => ({
        ...prev,
        requiresMfa: true,
        mfaToken: data.mfaToken,
      }));
      // Return so the caller (LoginPage) can show the MFA form instead
      // of running the leave-veil exit transition.
      return { requiresMfa: true };
    }

    // Force password reset.
    //
    // When the backend supplies a token (EmailConfirmed=true branch),
    // redirect to /reset-password with it so the user can set a new
    // password immediately. When NO token is supplied (the
    // EmailConfirmed=false branch — typical new-tenant signup where
    // the owner hasn't clicked the welcome-email link yet), redirecting
    // would land them on a broken page that submits an empty token and
    // gets "This reset link has expired" from the backend. Instead,
    // bubble the backend's message ("Please check your email…") up to
    // LoginPage so it can render it inline. Bug surfaced 2026-06-03 —
    // diagnostic-token entries showed two 400s on /api/auth/reset-password
    // immediately after a successful tenant create + login.
    if (data.mustResetPassword) {
      if (data.passwordResetToken) {
        const resetUrl = `/reset-password?email=${encodeURIComponent(email)}&token=${encodeURIComponent(data.passwordResetToken)}`;
        window.location.href = resetUrl;
        return { requiresMfa: false };
      }
      return {
        requiresMfa: false,
        checkEmailMessage: data.message
          || 'Please check your email and click the link to set your password before logging in.',
      };
    }

    // Store MFA flags for the banner — clear stale values when MFA is no longer suggested
    if (data.mfaSuggested === false) {
      sessionStorage.removeItem('mfaSuggested');
      sessionStorage.removeItem('mfaRequired');
    } else {
      sessionStorage.setItem('mfaSuggested', String(data.mfaSuggested || false));
      sessionStorage.setItem('mfaRequired', String(data.mfaRequired || false));
    }

    // Admin MFA required — tokens are issued but user must set up MFA.
    // Skip the forced /mfa-setup redirect on auth-handoff flows so the user
    // lands back on the consent screen; they can set up MFA later.
    const onAuthHandoffFlow =
      window.location.pathname.startsWith('/oauth/authorize')
      || window.location.pathname.startsWith('/oauth/desktop');
    if (data.mfaRequired && data.accessToken && !onAuthHandoffFlow) {
      applyTokenResponse(data);
      setTimeout(() => { window.location.href = '/mfa-setup'; }, 100);
      return { requiresMfa: false };
    }

    applyTokenResponse(data);
    // applyTokenResponse handles clearing the per-day "brief
    // dismissed" flag for fresh sign-ins, so Executives bounce
    // through /brief on every login regardless of where they were
    // earlier today.
    // Default: send the new session to home so they start on the role-appropriate
    // page. Exception: if they're partway through an OAuth/desktop authorize
    // flow (came in via Claude Desktop "Add custom connector" or the Cadence
    // Mac app's browser-handoff sign-in), keep the URL + query string so the
    // consent page can pick the request back up.
    if (!onAuthHandoffFlow && window.location.pathname !== '/') {
      window.history.replaceState(null, '', '/');
    }
    return { requiresMfa: false };
  }, [applyTokenResponse]);

  const verifyMfa = useCallback(async (code: string) => {
    const { data } = await axios.post(`${API_URL}/auth/mfa/verify`, {
      mfaToken: state.mfaToken,
      code,
    });
    // MFA verified successfully — clear suggestion/required flags
    sessionStorage.removeItem('mfaSuggested');
    sessionStorage.removeItem('mfaRequired');
    // applyTokenResponse handles the brief-dismissed flag reset.
    applyTokenResponse(data);
  }, [state.mfaToken, applyTokenResponse]);

  const logout = useCallback(() => {
    setState({
      accessToken: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      requiresMfa: false,
      mfaToken: null,
    });
    sessionStorage.removeItem('auth');
    clearStoredPrincipal();
    // Navigate to root so next login starts fresh — *unless* we're partway
    // through an OAuth or desktop authorize flow, in which case wiping the
    // URL kills the pending request from the MCP client or Cadence Mac app.
    // Same exception that login() makes when the session token expires mid-
    // consent (which used to bounce the user to / with no query string and
    // an "Unknown client_id" / "no session id" error on the empty re-mount).
    const onAuthHandoffFlow =
      window.location.pathname.startsWith('/oauth/authorize')
      || window.location.pathname.startsWith('/oauth/desktop');
    if (!onAuthHandoffFlow) {
      window.history.replaceState(null, '', '/');
    }
  }, []);

  // Singleton in-flight refresh promise. Multiple API calls that ALL see
  // an expired token at the same moment must share ONE POST /auth/refresh,
  // otherwise refresh-token rotation drops every token except the first
  // server response, and every concurrent caller after that one fails →
  // a forced logout for a still-valid session. Set the ref before awaiting
  // the network call so subsequent callers see the in-flight promise; clear
  // it in finally so the next genuine expiry can refresh again.
  const refreshInFlightRef = useRef<Promise<string> | null>(null);
  const ensureValidToken = useCallback(async (): Promise<string> => {
    if (state.accessToken && !isTokenExpired(state.accessToken)) {
      return state.accessToken;
    }

    if (!state.accessToken || !state.refreshToken) {
      logout();
      throw new Error('Session expired');
    }

    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current;
    }

    const refreshPromise = (async () => {
      try {
        const { data } = await axios.post(`${API_URL}/auth/refresh`, {
          accessToken: state.accessToken,
          refreshToken: state.refreshToken,
        });
        applyTokenResponse(data);
        return data.accessToken as string;
      } catch {
        logout();
        throw new Error('Session expired');
      }
    })();

    refreshInFlightRef.current = refreshPromise;
    try {
      return await refreshPromise;
    } finally {
      refreshInFlightRef.current = null;
    }
  }, [state.accessToken, state.refreshToken, logout, applyTokenResponse]);

  // Bootstrap the facts the token responses don't carry — the onboarding flag
  // and whether MFA is on — from `/auth/me`, once per signed-in user (every
  // sign-in path lands here with them unknown). Guarded by user id rather than
  // cancelled on re-render: the token refresh this can trigger replaces the
  // user object, and that used to throw the answer away. Failures leave
  // onboarding fail-open (below) and MFA unknown (no MFA prompt).
  useEffect(() => {
    if (!state.isAuthenticated || !state.user) { userFactsFetchedForRef.current = null; return; }
    const { id, onboardingCompletedAt, mfaEnabled } = state.user;
    if (onboardingCompletedAt !== undefined && mfaEnabled !== undefined) return; // already known
    if (userFactsFetchedForRef.current === id) return;
    userFactsFetchedForRef.current = id;
    (async () => {
      try {
        const token = await ensureValidToken();
        const { data } = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setState(prev => prev.user?.id === id
          ? {
            ...prev,
            user: {
              ...prev.user,
              onboardingCompletedAt: data.onboardingCompletedAt ?? null,
              mfaEnabled: typeof data.mfaEnabled === 'boolean' ? data.mfaEnabled : prev.user.mfaEnabled,
            },
          }
          : prev);
      } catch {
        // Fail OPEN — a transient /me error must not trap the user on the
        // home loader (or wrongly force setup). Resolve to a non-null value so
        // the gate releases to normal routing; a genuinely-new user gets routed
        // to onboarding on the next clean load.
        setState(prev => prev.user?.id === id
          ? { ...prev, user: { ...prev.user, onboardingCompletedAt: prev.user.onboardingCompletedAt ?? new Date(0).toISOString() } }
          : prev);
      }
    })();
  }, [state.isAuthenticated, state.user, ensureValidToken]);

  return (
    <AuthContext.Provider value={{ ...state, login, verifyMfa, logout, ensureValidToken, markOnboardingComplete, markMfaEnabled, applyTokens: applyTokenResponse }}>
      {children}
    </AuthContext.Provider>
  );
}
